import React, { FormEvent, useState } from "react";
import { CheckCircle2, Printer, Download } from "lucide-react";
import { ValidationErrors } from "src/errors";
import {
  RelabelCaseBuilderFormData,
  RelabelCaseBuilderFormDataKey,
  RelabelCaseRecord,
  CaseRow,
  BrowserPrintDevice
} from "src/types";
import { useScrollOnMessage } from "src/hooks";
import { FormLvlErrorMessage, ValidationErrorMessage } from "src/components/ui/feedback";
import { RelabelProductDetailsTable } from "./relabelProductDetailsTable";
import { CaseBuilderSection } from "./caseBuilderSection";
import { SubmittedCaseCard } from "./submittedCaseCard";
import { PrintLabelModal } from "./printLabelModal";

interface ZebraPrinterState {
  printer: BrowserPrintDevice | null;
  availablePrinters: BrowserPrintDevice[];
  isConnected: boolean;
  error: string | null;
  selectPrinter: (uid: string) => void;
}

interface RelabelCaseBuilderFormProps {
  formData: RelabelCaseBuilderFormData;
  validationErrors?: ValidationErrors<RelabelCaseBuilderFormDataKey>;
  formLvlErrorMessage: string;
  isFormComplete: boolean;
  isFormUpdated: boolean;
  submittedRelabelCases?: RelabelCaseRecord[];
  handleCasesChange: (cases: CaseRow[]) => void;
  handleSubmit: (e?: FormEvent) => Promise<void>;
  onDownloadLabel: (caseId: string) => Promise<void>;
  onDownloadAllLabels: () => Promise<void>;
  isDownloadingAll: boolean;
  onPrintLabel: (caseId: string) => Promise<void>;
  zebraPrinter: ZebraPrinterState;
  printedCases?: Record<string, { printerName: string }>;
  onEditRelabelProductQty?: (productId: string) => void;
  onEditCaseProduct?: (caseId: string, productId: string) => void;
}

const RelabelCaseBuilderForm: React.FC<RelabelCaseBuilderFormProps> = ({
  formData,
  validationErrors,
  formLvlErrorMessage,
  isFormComplete,
  isFormUpdated,
  submittedRelabelCases,
  handleCasesChange,
  handleSubmit,
  onDownloadLabel,
  onDownloadAllLabels,
  isDownloadingAll,
  onPrintLabel,
  zebraPrinter,
  printedCases,
  onEditRelabelProductQty,
  onEditCaseProduct
}) => {
  const formLvlErrorMessageRef = useScrollOnMessage(formLvlErrorMessage);
  const hasPreviousCases = !!submittedRelabelCases && submittedRelabelCases.length > 0;
  const fullyDistributed =
    formData.relabelProducts.length > 0 && formData.relabelProducts.every((p) => p.existingCasedQty >= p.euReceivedQty);

  const totalCases = formData.cases.filter((c) => c.products.some((p) => Number(p.quantity) > 0)).length;
  const sourceOrderId = formData.purchaseOrderId ?? formData.transferOrderId ?? "";

  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [casesToPrint, setCasesToPrint] = useState<RelabelCaseRecord[]>([]);

  const openPrintModal = (cases: RelabelCaseRecord[]) => {
    setCasesToPrint(cases);
    setPrintModalOpen(true);
  };

  return (
    <div className="px-2 sm:px-3 lg:px-4">
      <div className="mx-auto">
        <div className="overflow-hidden rounded-2xl border border-secondary-200 shadow-soft bg-white">
          <form onSubmit={handleSubmit} autoComplete="off" className="p-6 sm:p-8">
            <div className="space-y-6">
              <RelabelProductDetailsTable
                relabelProducts={formData.relabelProducts}
                onEditProductQty={onEditRelabelProductQty}
              />

              {!fullyDistributed && (
                <>
                  <CaseBuilderSection
                    purchaseOrderId={sourceOrderId}
                    relabelProducts={formData.relabelProducts}
                    cases={formData.cases}
                    onCasesChange={handleCasesChange}
                    isReadOnly={false}
                  />

                  <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("cases")} />
                </>
              )}

              {fullyDistributed && (
                <div className="rounded-xl border border-secondary-200 bg-secondary-50 px-4 py-3 text-sm text-secondary-700">
                  All EU-received units for this PO have been cartonised. Receive more stock in the EU Relabel Check-In
                  to build additional cases.
                </div>
              )}

              <FormLvlErrorMessage message={formLvlErrorMessage} centred={false} ref={formLvlErrorMessageRef} />

              {!fullyDistributed && (
                <div className="flex items-center gap-4">
                  <button
                    className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                    disabled={!isFormComplete || !isFormUpdated}
                    type="submit"
                  >
                    {`Generate ${totalCases} Case Label${totalCases !== 1 ? "s" : ""}`}
                  </button>
                </div>
              )}

              {hasPreviousCases && (
                <div className="space-y-3">
                  <div className="flex items-start gap-3 rounded-xl border border-success-200 bg-success-50 p-5">
                    <CheckCircle2 className="w-6 h-6 text-success-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-base font-bold text-success-800">
                        {submittedRelabelCases?.length} Previous Case
                        {submittedRelabelCases?.length !== 1 ? "s" : ""}
                      </p>
                      <p className="text-sm text-success-700 mt-1">
                        Cases already generated for {sourceOrderId}. Print/download labels below or build more cases
                        above as more EU stock arrives.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {[...(submittedRelabelCases ?? [])]
                      .sort((a, b) => a.caseNumber - b.caseNumber)
                      .map((relabelCase) => (
                        <SubmittedCaseCard
                          key={relabelCase.id}
                          relabelCaseRecord={relabelCase}
                          onDownloadLabel={onDownloadLabel}
                          onPrintLabel={() => openPrintModal([relabelCase])}
                          isPrinterConnected={zebraPrinter.isConnected}
                          printedInfo={printedCases?.[relabelCase.id]}
                          onEditProduct={onEditCaseProduct}
                        />
                      ))}
                  </div>

                  {submittedRelabelCases && submittedRelabelCases.length > 1 && (
                    <div className="flex justify-end gap-3 pt-2">
                      <button
                        type="button"
                        onClick={onDownloadAllLabels}
                        disabled={isDownloadingAll}
                        className="flex items-center gap-2 px-5 py-2.5 bg-white text-black text-sm font-semibold border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {isDownloadingAll ? (
                          <>
                            <Download className="w-4 h-4 animate-bounce" />
                            Downloading...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4" />
                            Download All Labels
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          openPrintModal([...submittedRelabelCases].sort((a, b) => a.caseNumber - b.caseNumber))
                        }
                        className="flex items-center gap-2 px-5 py-2.5 bg-black text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition-colors"
                      >
                        <Printer className="w-4 h-4" />
                        ZPL Print All
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </form>
        </div>
      </div>

      <PrintLabelModal
        open={printModalOpen}
        onClose={() => setPrintModalOpen(false)}
        casesToPrint={casesToPrint}
        printer={zebraPrinter.printer}
        availablePrinters={zebraPrinter.availablePrinters}
        isPrinterConnected={zebraPrinter.isConnected}
        printerError={zebraPrinter.error}
        onSelectPrinter={zebraPrinter.selectPrinter}
        onPrint={onPrintLabel}
      />
    </div>
  );
};

export { RelabelCaseBuilderForm };
